// frontend/src/pages/Reinsurance/components/ContractManagement/ContractCreate/index.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faSave, faTimes, faChevronRight, 
  faChevronLeft, faExclamationCircle, faCheck,
  faSpinner, faSearch
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useContract } from '../../../contexts/ContractContext';
import { reinsuranceService } from '../../../../../services/reinsurance';
import Input from '../../../../../components/form/Input/Input';
import Dropdown from '../../../../../components/form/Dropdown/Dropdown';
import TextArea from '../../../../../components/form/TextArea/TextArea';
import Checkbox from '../../../../../components/form/Checkbox/Checkbox';
import styles from './ContractCreate.module.css';

const ContractCreate = ({ onBack, onSuccess }) => {
  const { t } = useLanguage();
  const { createContract, verifyRNR, error: apiError, clearError } = useContract();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [isVerifyingRNR, setIsVerifyingRNR] = useState(false);
  const [rnrVerified, setRnrVerified] = useState(false);
  const [rnrError, setRnrError] = useState(null);
  
  // Form data state
  const [formData, setFormData] = useState({
    // Basic Information
    contract_number: '',
    rnr: '',
    contract_type: '',
    contract_subtype: '',
    contract_structure: '',
    
    // Cedent Information
    cedent: {
      cedent_id: '',
      cedent_name: '',
      cedent_domicile: '',
      cedent_rating: {
        am_best: '',
        sp: '',
        moodys: '',
        fitch: ''
      }
    },
    
    // Broker Information
    broker: {
      broker_id: '',
      broker_name: '',
      broker_contact: {
        name: '',
        email: '',
        phone: ''
      },
      broker_commission: {
        percentage: '',
        currency: 'USD'
      }
    },
    
    // Contract Terms
    terms: {
      effective_date: '',
      expiration_date: '',
      currency: 'USD',
      limit: '',
      attachment_point: '',
      premium: {
        base_amount: '',
        adjustable: false,
        adjustment_basis: '',
        minimum_premium: '',
        deposit_premium: ''
      }
    },
    
    // Risk Information
    risk: {
      covered_perils: [],
      territories: [],
      expected_loss: '',
      probable_maximum_loss: ''
    }
  });
  
  // Clear API errors when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);
  
  // Options for dropdowns - using reinsuranceService
  const contractTypeOptions = [
    { value: '', labelKey: 'select_contract_type' },
    ...reinsuranceService.getContractTypeOptions().map(option => ({
      value: option.value,
      labelKey: option.value // Using the value as the translation key
    }))
  ];
  
  const contractStructureOptions = [
    { value: '', labelKey: 'select_contract_structure' },
    ...reinsuranceService.getContractStructureOptions().map(option => ({
      value: option.value,
      labelKey: option.value // Using the value as the translation key
    }))
  ];
  
  const contractSubtypeOptions = {
    property: [
      { value: 'catastrophe', labelKey: 'catastrophe' },
      { value: 'per_risk', labelKey: 'per_risk' },
      { value: 'aggregate', labelKey: 'aggregate' }
    ],
    specialty: [
      { value: 'marine', labelKey: 'marine' },
      { value: 'aviation', labelKey: 'aviation' },
      { value: 'energy', labelKey: 'energy' },
      { value: 'cyber', labelKey: 'cyber' }
    ],
    parametric: [
      { value: 'earthquake', labelKey: 'earthquake' },
      { value: 'hurricane', labelKey: 'hurricane' },
      { value: 'weather', labelKey: 'weather' }
    ],
    facultative: [
      { value: 'property', labelKey: 'property' },
      { value: 'casualty', labelKey: 'casualty' }
    ],
    ilw: [
      { value: 'catastrophe', labelKey: 'catastrophe' },
      { value: 'specialty', labelKey: 'specialty' }
    ]
  };
  
  const currencyOptions = [
    { value: 'USD', labelKey: 'usd' },
    { value: 'EUR', labelKey: 'eur' },
    { value: 'GBP', labelKey: 'gbp' },
    { value: 'JPY', labelKey: 'jpy' }
  ];
  
  const adjustmentBasisOptions = [
    { value: '', labelKey: 'select_adjustment_basis' },
    { value: 'Exposure', labelKey: 'exposure' },
    { value: 'Premium Income', labelKey: 'premium_income' },
    { value: 'Loss Experience', labelKey: 'loss_experience' }
  ];
  
  const perilOptions = [
    { value: 'hurricane', labelKey: 'hurricane' },
    { value: 'earthquake', labelKey: 'earthquake' },
    { value: 'flood', labelKey: 'flood' },
    { value: 'wildfire', labelKey: 'wildfire' },
    { value: 'tornado', labelKey: 'tornado' },
    { value: 'winter_storm', labelKey: 'winter_storm' },
    { value: 'hail', labelKey: 'hail' },
    { value: 'tsunami', labelKey: 'tsunami' }
  ];
  
  const territoryOptions = [
    { value: 'US', labelKey: 'united_states' },
    { value: 'CA', labelKey: 'canada' },
    { value: 'EU', labelKey: 'europe' },
    { value: 'JP', labelKey: 'japan' },
    { value: 'AU', labelKey: 'australia' },
    { value: 'NZ', labelKey: 'new_zealand' },
    { value: 'GLOBAL', labelKey: 'global' }
  ];
  
  // Handle RNR verification
  const handleVerifyRNR = async () => {
    if (!formData.rnr) {
      setRnrError('rnr_required');
      return;
    }
    
    setIsVerifyingRNR(true);
    setRnrError(null);
    
    try {
      const response = await verifyRNR(formData.rnr);
      
      if (response.success) {
        setRnrVerified(true);
        
        // Auto-fill some fields if available in the response
        if (response.cedent_name) {
          setFormData(prev => ({
            ...prev,
            cedent: {
              ...prev.cedent,
              cedent_name: response.cedent_name
            }
          }));
        }
      } else {
        setRnrVerified(false);
        setRnrError(response.message || 'rnr_verification_failed');
      }
    } catch (err) {
      setRnrVerified(false);
      setRnrError(err.message || 'rnr_verification_failed');
    } finally {
      setIsVerifyingRNR(false);
    }
  };
  
  // Handle form field changes
  const handleChange = (field, value) => {
    // Reset RNR verification when RNR changes
    if (field === 'rnr') {
      setRnrVerified(false);
      setRnrError(null);
    }
    
    // Handle nested properties
    if (field.includes('.')) {
      const [parent, child, grandchild] = field.split('.');
      
      if (grandchild) {
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: {
              ...prev[parent][child],
              [grandchild]: value
            }
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        }));
      }
    } else {
      // Handle simple properties
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
      
      // Reset subtype when contract type changes
      if (field === 'contract_type') {
        setFormData(prev => ({
          ...prev,
          contract_subtype: ''
        }));
      }
    }
  };
  
  // Validate current step
  const validateStep = (step) => {
    switch (step) {
      case 1: // Basic Information
        if (!formData.contract_number) return 'contract_number_required';
        if (!formData.rnr) return 'rnr_required';
        if (!rnrVerified) return 'rnr_verification_required';
        if (!formData.contract_type) return 'contract_type_required';
        if (!formData.contract_structure) return 'contract_structure_required';
        return null;
        
      case 2: // Cedent Information
        if (!formData.cedent.cedent_name) return 'cedent_name_required';
        return null;
        
      case 3: // Contract Terms
        if (!formData.terms.effective_date) return 'effective_date_required';
        if (!formData.terms.expiration_date) return 'expiration_date_required';
        if (!formData.terms.limit) return 'limit_required';
        if (!formData.terms.premium.base_amount) return 'premium_required';
        
        // Check if expiration date is after effective date
        if (new Date(formData.terms.expiration_date) <= new Date(formData.terms.effective_date)) {
          return 'expiration_date_must_be_after_effective_date';
        }
        
        return null;
        
      case 4: // Risk Information
        if (formData.risk.covered_perils.length === 0) return 'covered_perils_required';
        if (formData.risk.territories.length === 0) return 'territories_required';
        return null;
        
      default:
        return null;
    }
  };
  
  // Handle next step
  const handleNextStep = () => {
    const error = validateStep(currentStep);
    
    if (error) {
      setFormError(error);
      return;
    }
    
    setFormError(null);
    setCurrentStep(prev => prev + 1);
  };
  
  // Handle previous step
  const handlePrevStep = () => {
    setFormError(null);
    setCurrentStep(prev => prev - 1);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate final step
    const error = validateStep(currentStep);
    if (error) {
      setFormError(error);
      return;
    }
    
    setIsSubmitting(true);
    setFormError(null);
    
    try {
      // Format data for API
      const contractData = {
        ...formData,
        // Convert string values to numbers where needed
        terms: {
          ...formData.terms,
          limit: parseFloat(formData.terms.limit),
          attachment_point: formData.terms.attachment_point ? parseFloat(formData.terms.attachment_point) : 0,
          premium: {
            ...formData.terms.premium,
            base_amount: parseFloat(formData.terms.premium.base_amount),
            minimum_premium: formData.terms.premium.minimum_premium ? parseFloat(formData.terms.premium.minimum_premium) : null,
            deposit_premium: formData.terms.premium.deposit_premium ? parseFloat(formData.terms.premium.deposit_premium) : null
          }
        },
        risk: {
          ...formData.risk,
          expected_loss: formData.risk.expected_loss ? parseFloat(formData.risk.expected_loss) : null,
          probable_maximum_loss: formData.risk.probable_maximum_loss ? parseFloat(formData.risk.probable_maximum_loss) : null
        },
        broker: {
          ...formData.broker,
          broker_commission: {
            ...formData.broker.broker_commission,
            percentage: formData.broker.broker_commission.percentage ? parseFloat(formData.broker.broker_commission.percentage) : null,
            amount: formData.broker.broker_commission.percentage ? 
              (parseFloat(formData.broker.broker_commission.percentage) / 100) * parseFloat(formData.terms.premium.base_amount) : null
          }
        }
      };
      
      const response = await createContract(contractData);
      
      if (response.success) {
        onSuccess(response.data.contract_id);
      } else {
        throw new Error(response.error || 'Failed to create contract');
      }
    } catch (err) {
      console.error('Error creating contract:', err);
      setFormError(err.message || 'Failed to create contract. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>{t('basic_information')}</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="contract_number"
                  labelKey="contract_number"
                  placeholderKey="enter_contract_number"
                  value={formData.contract_number}
                  onChange={(value) => handleChange('contract_number', value)}
                  required
                />
              </div>
              
              <div className={styles.formGroup}>
                <div className={styles.rnrInputGroup}>
                  <Input
                    id="rnr"
                    labelKey="rnr"
                    placeholderKey="enter_rnr"
                    value={formData.rnr}
                    onChange={(value) => handleChange('rnr', value)}
                    required
                    errorKey={rnrError}
                  />
                  <button 
                    type="button" 
                    className={styles.verifyButton}
                    onClick={handleVerifyRNR}
                    disabled={isVerifyingRNR || !formData.rnr}
                  >
                    {isVerifyingRNR ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={faSearch} />
                    )}
                    <span>{t('verify')}</span>
                  </button>
                </div>
                {rnrVerified && (
                  <div className={styles.verifiedBadge}>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>{t('rnr_verified')}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Dropdown
                  id="contract_type"
                  labelKey="contract_type"
                  options={contractTypeOptions}
                  value={formData.contract_type}
                  onChange={(value) => handleChange('contract_type', value)}
                  required
                  direction='up'
                />
              </div>
              
              <div className={styles.formGroup}>
                <Dropdown
                  id="contract_structure"
                  labelKey="contract_structure"
                  options={contractStructureOptions}
                  value={formData.contract_structure}
                  onChange={(value) => handleChange('contract_structure', value)}
                  required
                  direction='up'
                />
              </div>
            </div>
            
            {formData.contract_type && (
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <Dropdown
                    id="contract_subtype"
                    labelKey="contract_subtype"
                    options={[
                      { value: '', labelKey: 'select_contract_subtype' },
                      ...(contractSubtypeOptions[formData.contract_type] || [])
                    ]}
                    value={formData.contract_subtype}
                    onChange={(value) => handleChange('contract_subtype', value)}
                    direction='up'
                  />
                </div>
              </div>
            )}
          </div>
        );
        
      case 2:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>{t('cedent_information')}</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="cedent_name"
                  labelKey="cedent_name"
                  placeholderKey="enter_cedent_name"
                  value={formData.cedent.cedent_name}
                  onChange={(value) => handleChange('cedent.cedent_name', value)}
                  required
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="cedent_domicile"
                  labelKey="cedent_domicile"
                  placeholderKey="enter_cedent_domicile"
                  value={formData.cedent.cedent_domicile}
                  onChange={(value) => handleChange('cedent.cedent_domicile', value)}
                />
              </div>
            </div>
            
            <h3 className={styles.sectionTitle}>{t('cedent_ratings')}</h3>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="am_best"
                  labelKey="am_best_rating"
                  placeholderKey="enter_am_best_rating"
                  value={formData.cedent.cedent_rating.am_best}
                  onChange={(value) => handleChange('cedent.cedent_rating.am_best', value)}
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="sp"
                  labelKey="sp_rating"
                  placeholderKey="enter_sp_rating"
                  value={formData.cedent.cedent_rating.sp}
                  onChange={(value) => handleChange('cedent.cedent_rating.sp', value)}
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="moodys"
                  labelKey="moodys_rating"
                  placeholderKey="enter_moodys_rating"
                  value={formData.cedent.cedent_rating.moodys}
                  onChange={(value) => handleChange('cedent.cedent_rating.moodys', value)}
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="fitch"
                  labelKey="fitch_rating"
                  placeholderKey="enter_fitch_rating"
                  value={formData.cedent.cedent_rating.fitch}
                  onChange={(value) => handleChange('cedent.cedent_rating.fitch', value)}
                />
              </div>
            </div>
            
            <h3 className={styles.sectionTitle}>{t('broker_information')}</h3>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="broker_name"
                  labelKey="broker_name"
                  placeholderKey="enter_broker_name"
                  value={formData.broker.broker_name}
                  onChange={(value) => handleChange('broker.broker_name', value)}
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="broker_commission"
                  labelKey="broker_commission_percentage"
                  placeholderKey="enter_broker_commission"
                  value={formData.broker.broker_commission.percentage}
                  onChange={(value) => handleChange('broker.broker_commission.percentage', value)}
                  type="number"
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="broker_contact_name"
                  labelKey="broker_contact_name"
                  placeholderKey="enter_broker_contact_name"
                  value={formData.broker.broker_contact.name}
                  onChange={(value) => handleChange('broker.broker_contact.name', value)}
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="broker_contact_email"
                  labelKey="broker_contact_email"
                  placeholderKey="enter_broker_contact_email"
                  value={formData.broker.broker_contact.email}
                  onChange={(value) => handleChange('broker.broker_contact.email', value)}
                  type="email"
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="broker_contact_phone"
                  labelKey="broker_contact_phone"
                  placeholderKey="enter_broker_contact_phone"
                  value={formData.broker.broker_contact.phone}
                  onChange={(value) => handleChange('broker.broker_contact.phone', value)}
                />
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>{t('contract_terms')}</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="effective_date"
                  labelKey="effective_date"
                  placeholderKey="select_effective_date"
                  value={formData.terms.effective_date}
                  onChange={(value) => handleChange('terms.effective_date', value)}
                  type="date"
                  required
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="expiration_date"
                  labelKey="expiration_date"
                  placeholderKey="select_expiration_date"
                  value={formData.terms.expiration_date}
                  onChange={(value) => handleChange('terms.expiration_date', value)}
                  type="date"
                  required
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Dropdown
                  id="currency"
                  labelKey="currency"
                  options={currencyOptions}
                  value={formData.terms.currency}
                  onChange={(value) => handleChange('terms.currency', value)}
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="limit"
                  labelKey="limit"
                  placeholderKey="enter_limit"
                  value={formData.terms.limit}
                  onChange={(value) => handleChange('terms.limit', value)}
                  type="number"
                  required
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="attachment_point"
                  labelKey="attachment_point"
                  placeholderKey="enter_attachment_point"
                  value={formData.terms.attachment_point}
                  onChange={(value) => handleChange('terms.attachment_point', value)}
                  type="number"
                />
              </div>
            </div>
            
            <h3 className={styles.sectionTitle}>{t('premium_details')}</h3>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="base_premium"
                  labelKey="base_premium"
                  placeholderKey="enter_base_premium"
                  value={formData.terms.premium.base_amount}
                  onChange={(value) => handleChange('terms.premium.base_amount', value)}
                  type="number"
                  required
                />
              </div>
              
              <div className={styles.formGroup}>
                <Checkbox
                  id="adjustable_premium"
                  labelKey="adjustable_premium"
                  checked={formData.terms.premium.adjustable}
                  onChange={(value) => handleChange('terms.premium.adjustable', value)}
                />
              </div>
            </div>
            
            {formData.terms.premium.adjustable && (
              <>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <Dropdown
                      id="adjustment_basis"
                      labelKey="adjustment_basis"
                      options={adjustmentBasisOptions}
                      value={formData.terms.premium.adjustment_basis}
                      onChange={(value) => handleChange('terms.premium.adjustment_basis', value)}
                    />
                  </div>
                </div>
                
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <Input
                      id="minimum_premium"
                      labelKey="minimum_premium"
                      placeholderKey="enter_minimum_premium"
                      value={formData.terms.premium.minimum_premium}
                      onChange={(value) => handleChange('terms.premium.minimum_premium', value)}
                      type="number"
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <Input
                      id="deposit_premium"
                      labelKey="deposit_premium"
                      placeholderKey="enter_deposit_premium"
                      value={formData.terms.premium.deposit_premium}
                      onChange={(value) => handleChange('terms.premium.deposit_premium', value)}
                      type="number"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        );
        
      case 4:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>{t('risk_information')}</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Dropdown
                  id="covered_perils"
                  labelKey="covered_perils"
                  options={perilOptions}
                  value={formData.risk.covered_perils}
                  onChange={(value) => handleChange('risk.covered_perils', value)}
                  multiple
                  required
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Dropdown
                  id="territories"
                  labelKey="territories"
                  options={territoryOptions}
                  value={formData.risk.territories}
                  onChange={(value) => handleChange('risk.territories', value)}
                  multiple
                  required
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="expected_loss"
                  labelKey="expected_loss"
                  placeholderKey="enter_expected_loss"
                  value={formData.risk.expected_loss}
                  onChange={(value) => handleChange('risk.expected_loss', value)}
                  type="number"
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="probable_maximum_loss"
                  labelKey="probable_maximum_loss"
                  placeholderKey="enter_probable_maximum_loss"
                  value={formData.risk.probable_maximum_loss}
                  onChange={(value) => handleChange('risk.probable_maximum_loss', value)}
                  type="number"
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <TextArea
                  id="risk_notes"
                  labelKey="risk_notes"
                  placeholderKey="enter_risk_notes"
                  value={formData.risk.notes || ''}
                  onChange={(value) => handleChange('risk.notes', value)}
                  rows={4}
                />
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className={styles.contractCreate}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onBack} disabled={isSubmitting}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>{t('back_to_contracts')}</span>
        </button>
        <h1 className={styles.title}>{t('create_contract')}</h1>
      </div>
      
      {(formError || apiError) && (
        <div className={styles.error}>
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>{t(formError) || apiError}</span>
        </div>
      )}
      
      <div className={styles.stepIndicator}>
        {[1, 2, 3, 4].map((step) => (
          <div 
            key={step} 
            className={`${styles.step} ${currentStep === step ? styles.active : ''} ${currentStep > step ? styles.completed : ''}`}
          >
            <div className={styles.stepNumber}>
              {currentStep > step ? (
                <FontAwesomeIcon icon={faCheck} />
              ) : (
                step
              )}
            </div>
            <div className={styles.stepLabel}>
              {t(
                step === 1 ? 'basic_information' :
                step === 2 ? 'cedent_information' :
                step === 3 ? 'contract_terms' :
                'risk_information'
              )}
            </div>
          </div>
        ))}
      </div>
      
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formContent}>
          {renderStepContent()}
        </div>
        
        <div className={styles.formActions}>
          {currentStep > 1 && (
            <button 
              type="button" 
              className={styles.prevButton} 
              onClick={handlePrevStep}
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              <span>{t('previous')}</span>
            </button>
          )}
          
          {currentStep < 4 ? (
            <button 
              type="button" 
              className={styles.nextButton}
              onClick={handleNextStep}
            >
              <span>{t('next')}</span>
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          ) : (
            <>
              <button 
                type="button" 
                className={styles.cancelButton} 
                onClick={onBack}
                disabled={isSubmitting}
              >
                <FontAwesomeIcon icon={faTimes} />
                <span>{t('cancel')}</span>
              </button>
              
              <button 
                type="submit" 
                className={styles.saveButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className={styles.spinner}></div>
                    <span>{t('saving')}</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} />
                    <span>{t('save_contract')}</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default ContractCreate;